import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPostos } from '../mockPostos';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, MapPin, Zap, Battery, Calendar, Activity, Wrench } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PostoDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const posto = mockPostos.find(p => p.id === id);

  if (!posto) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Posto não encontrado</p>
        <Button onClick={() => navigate('/carregamento/postos')} className="mt-4">
          Voltar à lista
        </Button>
      </div>
    );
  }

  const getEstadoBadge = (estado) => {
    const variants = {
      'Disponível': 'bg-green-100 text-green-800 border-green-200',
      'Em Uso': 'bg-blue-100 text-blue-800 border-blue-200',
      'Manutenção': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return variants[estado] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Dados de exemplo para o gráfico
  const chartData = [
    { mes: 'Jun', utilizacao: 78 },
    { mes: 'Jul', utilizacao: 82 },
    { mes: 'Ago', utilizacao: 88 },
    { mes: 'Set', utilizacao: 85 },
    { mes: 'Out', utilizacao: 90 },
    { mes: 'Nov', utilizacao: posto.utilizacaoMensal },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/carregamento/postos')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{posto.nome} - {posto.local}</h1>
          <p className="text-gray-600 mt-1">Informação detalhada do posto de carregamento</p>
        </div>
        <Badge className={getEstadoBadge(posto.estado)}>
          {posto.estado}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Básicas */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Técnicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Local:
              </span>
              <span className="text-sm font-semibold">{posto.local}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <Battery className="h-4 w-4" />
                Tipo:
              </span>
              <span className="text-sm font-semibold">{posto.tipo}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Potência:
              </span>
              <span className="text-sm font-semibold">{posto.potencia} kW</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Fabricante:</span>
              <span className="text-sm font-semibold">{posto.fabricante}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Modelo:</span>
              <span className="text-sm font-semibold">{posto.modelo}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Nº Série:</span>
              <span className="text-sm font-semibold text-xs">{posto.numeroSerie}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Instalação:
              </span>
              <span className="text-sm font-semibold">{new Date(posto.dataInstalacao).toLocaleDateString('pt-PT')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Última Manutenção:
              </span>
              <span className="text-sm font-semibold">{new Date(posto.ultimaManutencao).toLocaleDateString('pt-PT')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Estatísticas de Utilização</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <p className="text-sm text-gray-600 mb-1">Utilização Mensal</p>
                <p className="text-3xl font-bold text-blue-600">{posto.utilizacaoMensal}%</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <p className="text-sm text-gray-600 mb-1">Total Carregamentos</p>
                <p className="text-3xl font-bold text-green-600">{posto.totalCarregamentos}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                <p className="text-sm text-gray-600 mb-1">Energia Fornecida</p>
                <p className="text-3xl font-bold text-orange-600">{posto.energiaFornecida.toLocaleString('pt-PT')} kWh</p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Evolução da Utilização (Últimos 6 meses)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Line type="monotone" dataKey="utilizacao" stroke="#3b82f6" strokeWidth={2} name="Utilização (%)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ações */}
      <Card>
        <CardHeader>
          <CardTitle>Ações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button variant="outline">
              <Activity className="h-4 w-4 mr-2" />
              Ver Histórico
            </Button>
            <Button variant="outline">
              <Wrench className="h-4 w-4 mr-2" />
              Agendar Manutenção
            </Button>
            <Button variant="outline">
              <Calendar className="h-4 w-4 mr-2" />
              Relatório Mensal
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PostoDetalhe;
